import { Chess, type Square as CjSquare, type PieceSymbol } from 'chess.js';
import type { ChessAdapter } from './adapter';
import type { BoardSnapshot, Move, Piece, Role, Square } from './types';
import { START_FEN } from './types';

const ROLE_MAP: Record<PieceSymbol, Role> = {
  p: 'p',
  n: 'n',
  b: 'b',
  r: 'r',
  q: 'q',
  k: 'k',
};

function toSquare(s: string): Square {
  return s as Square;
}

function toMove(m: {
  from: string;
  to: string;
  promotion?: string;
  san?: string;
  lan?: string;
}): Move {
  return {
    from: toSquare(m.from),
    to: toSquare(m.to),
    promotion: m.promotion as Role | undefined,
    san: m.san,
    uci: m.lan ?? `${m.from}${m.to}${m.promotion ?? ''}`,
  };
}

export function createChessJsAdapter(fen: string = START_FEN): ChessAdapter {
  const game = new Chess(fen);

  return {
    loadFen(next) {
      try {
        game.load(next);
      } catch {
        throw new Error(`Invalid FEN: ${next}`);
      }
    },

    tryLoadFen(next) {
      try {
        // chess.js throws on invalid; some builds return false
        const result = game.load(next) as unknown;
        if (result === false) return false;
        return true;
      } catch {
        return false;
      }
    },

    fen: () => game.fen(),

    snapshot(): BoardSnapshot {
      const pieces = new Map<Square, Piece>();
      for (const row of game.board()) {
        for (const cell of row) {
          if (!cell) continue;
          pieces.set(toSquare(cell.square), {
            role: ROLE_MAP[cell.type],
            color: cell.color,
          });
        }
      }
      return {
        fen: game.fen(),
        turn: game.turn(),
        pieces,
      };
    },

    moves(from?: Square): Move[] {
      const opts = from
        ? { square: from as CjSquare, verbose: true as const }
        : { verbose: true as const };
      return game.moves(opts).map(toMove);
    },

    move(move) {
      try {
        const result = game.move({
          from: move.from,
          to: move.to,
          promotion: move.promotion,
        });
        if (!result) return null;
        return toMove(result);
      } catch {
        return null;
      }
    },

    isCheck: () => game.isCheck(),
    isCheckmate: () => game.isCheckmate(),
    isDraw: () => game.isDraw(),
    isGameOver: () => game.isGameOver(),

    reset(next = START_FEN) {
      try {
        game.load(next);
      } catch {
        throw new Error(`Invalid FEN: ${next}`);
      }
    },
  };
}
